/**
 * **The Paper family** (Stream A, Rebuild 39's Paper.dc.html, on E01's print tokens): a sheet at
 * its real size, black-and-white safe, filled from the screen it names. A long list runs onto as
 * many pages as it needs, the signature lines on the last. A field with no source prints blank
 * with its label, never a sample value. The QR carries the id CIDER reads back and nothing else.
 */
import type { ReactNode } from 'react';
import { renderSVG } from 'uqr';
import { Sheet, formatDateTime, type SheetSize } from '@cider/ui';

/** A line on a sheet: a section head, or a row of cells (a cell `null` is a line to write on). */
export type SheetLine =
  { head: string } | { cells: readonly (string | null)[]; tick?: boolean; key?: string };

export interface SheetDoc {
  size: SheetSize;
  title: string;
  sub?: string;
  /** The id CIDER reads back, printed under the code. */
  qr?: string;
  /** The footer's left: the sheet's name and what it is for. */
  name: string;
  /** The column widths, by class: `wide` · `mid` · `fig` · `tick` · `when`. */
  cols?: readonly ('wide' | 'mid' | 'fig' | 'tick' | 'when')[];
  lines: readonly SheetLine[];
  /** The signature lines, on the last page. */
  sign?: readonly string[];
  /** What goes above the lines — a big word, a paragraph. */
  lead?: ReactNode;
}

const PER_PAGE: Record<SheetSize, number> = {
  letter: 30,
  half: 12,
  card: 14,
  'card-landscape': 8,
  badge: 6,
  tag: 3,
  poster: 40,
  slide: 8,
};

export const qrDrawing = (code: string): ReactNode => (
  <img src={`data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(code))}`} alt="" />
);

export function splitPages(lines: readonly SheetLine[], per: number): SheetLine[][] {
  const out: SheetLine[][] = [];
  let page: SheetLine[] = [];
  for (const l of lines) {
    if (page.length >= per) {
      out.push(page);
      // A section carried onto the next page says so.
      const lastHead = [...page].reverse().find((x): x is { head: string } => 'head' in x);
      page = lastHead && !('head' in l) ? [{ head: `${lastHead.head} · continued` }] : [];
    }
    page.push(l);
  }
  out.push(page);
  return out;
}

export function SheetPages({ doc }: { doc: SheetDoc }): ReactNode {
  const printed = formatDateTime(new Date());
  const all = splitPages(doc.lines, PER_PAGE[doc.size]);
  const cls = (i: number): string => `pr-c pr-c--${doc.cols?.[i] ?? 'wide'}`;
  return (
    <div className="pr-root" data-size={doc.size} data-pages={all.length} data-title={doc.title}>
      <style>{`@page { size: ${doc.size === 'letter' || doc.size === 'half' ? 'letter' : doc.size === 'slide' ? '10in 5.625in' : 'auto'}; margin: 0; }`}</style>
      {all.map((lines, p) => (
        <Sheet
          key={p}
          size={doc.size}
          title={
            all.length > 1
              ? `${doc.title} · page ${String(p + 1)} of ${String(all.length)}`
              : doc.title
          }
          sub={doc.sub}
          qr={doc.qr ? { drawing: qrDrawing(doc.qr), code: doc.qr } : undefined}
          name={doc.name}
          printed={printed}
        >
          {p === 0 ? doc.lead : null}
          {lines.map((l, i) =>
            'head' in l ? (
              <h2 key={`h${String(i)}`} className="pr-head">
                {l.head}
              </h2>
            ) : (
              <div key={l.key ?? String(i)} className="pr-row">
                {l.cells.map((c, j) => (
                  <span key={j} className={c === null ? `${cls(j)} pr-line` : cls(j)}>
                    {c ?? ''}
                  </span>
                ))}
                {l.tick ? <span className="pr-c pr-c--tick pr-box" aria-label="tick" /> : null}
              </div>
            ),
          )}
          {p === all.length - 1 && doc.sign?.length ? (
            <div className="pr-signs">
              {doc.sign.map((s) => (
                <span key={s} className="pr-sign">
                  {s}
                </span>
              ))}
            </div>
          ) : null}
        </Sheet>
      ))}
    </div>
  );
}
